<template>
  <Bar
    :data="finalChartData"
    :options="finalChartOptions"
    :plugins="plugins"
  />
</template>

<script lang="ts">
import 'chart.js/auto'
import { Bar } from 'vue-chartjs'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import type {
  ChartOptions,
  ChartData as FinalChartData,
  ChartDataset
} from 'chart.js/auto'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext } from '~/types/analyticsFormat'
import {
  buildOpenDrilldownEventBody,
  buildYScales,
  generateColorGradient,
  getColorGradientPalette
} from '~/services/chart'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import {
  COLOR_TRANSPARENCY_SUFFIX,
  DATALABELS_PLUGIN_OPTIONS,
  DEFAULT_CHART_OPTIONS,
  SCALES_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'
import type {
  ChartData,
  DataSet,
  OpenDrilldownEventBody
} from '~/types/analytics'
import type { ResultSetItemValue } from '~/types/query'

const BORDER_RADIUS = 4

export default {
  name: 'ChartBar',
  components: { Bar },
  props: {
    isStacked: {
      type: Boolean,
      required: true
    },
    isTimeQuery: {
      type: Boolean,
      required: true
    },
    showDatalabels: {
      type: Boolean,
      required: true
    },
    isMultiMeasures: {
      type: Boolean,
      required: true
    },
    chartData: {
      type: Object as PropType<ChartData>,
      required: true
    },
    isClickable: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'open-drilldown': (_: OpenDrilldownEventBody) => true
  },
  setup() {
    return { plugins: [DataLabelsPlugin] }
  },
  computed: {
    showLegend() {
      return this.isStacked || this.isMultiMeasures
    },
    finalChartOptions(): ChartOptions<'bar'> {
      const yScales = buildYScales({
        chartData: this.chartData,
        isStacked: this.isStacked,
        isMultiMeasures: this.isMultiMeasures
      })

      return {
        ...DEFAULT_CHART_OPTIONS,
        onHover: (event, chartElement) => {
          ;(event!.native!.target as HTMLElement).style.cursor =
            chartElement[0] && this.isClickable ? 'pointer' : 'default'
        },
        onClick: (clickEvent, _, chart) => {
          const eventBody = buildOpenDrilldownEventBody(
            clickEvent,
            chart,
            this.chartData
          )

          if (eventBody) {
            this.$emit('open-drilldown', eventBody)
          }
        },
        scales: {
          ...yScales,
          x: {
            ...SCALES_OPTIONS.x,
            stacked: this.isStacked,
            ticks: {
              ...SCALES_OPTIONS.x.ticks,
              display: true,
              font: {
                size: 14
              }
            }
          }
        },
        plugins: {
          legend: {
            display: this.showLegend,
            position: 'top',
            labels: {
              font: {
                size: 14
              },
              pointStyle: 'rectRounded',
              usePointStyle: true
            }
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              title: context => {
                const xLabel = context[0]?.label
                const stackLabel = (context[0]?.dataset as DataSet)
                  .groupValueLabel
                return this.isStacked ? `${xLabel} - ${stackLabel}` : xLabel
              },
              label: context => {
                const label = (context.dataset as DataSet).measureLabel
                const format = (context.dataset as DataSet).format
                const value = context.parsed.y
                return `${label}: ${formatMemberValue(
                  value,
                  format,
                  FormatContext.CHART,
                  false
                )}`
              }
            }
          },
          datalabels: {
            ...DATALABELS_PLUGIN_OPTIONS,
            color: this.showDatalabels ? '#FFFFFF' : 'transparent',
            formatter: (value, context) => {
              // Hide null values
              if (value === null) {
                return ''
              }
              const format = (context.dataset as DataSet).format
              return formatMemberValue(
                value,
                format,
                FormatContext.CHART,
                false
              )
            }
          }
        }
      }
    },
    finalChartData(): FinalChartData<'bar'> {
      const datasetsNumber = this.chartData.datasets.length
      const colors = getColorGradientPalette(
        datasetsNumber,
        Object.values(exportedColors)
      )

      const groupValues = this.chartData.datasets
        .map(dataset => dataset.groupValue)
        .filter(groupValue => groupValue !== undefined) as ResultSetItemValue[]

      return {
        ...this.chartData,
        labels: this.chartData.labels.map(label => label.formattedValue),
        datasets: this.chartData.datasets.map((dataset, datasetIndex) => {
          const j =
            this.isMultiMeasures && this.isStacked
              ? groupValues.indexOf(dataset.groupValue as ResultSetItemValue)
              : datasetIndex
          const color = colors[j % colors.length]!

          return {
            ...dataset,
            stack: dataset.measureTitle,
            yAxisID: datasetsNumber === 1 ? 'y' : `y${dataset.measureTitle}`,
            backgroundColor: context => {
              if (this.isStacked && this.isTimeQuery) {
                // add transparency if in future
                return this.chartData.labels.map(label =>
                  label.isInFuture
                    ? color.bottom + COLOR_TRANSPARENCY_SUFFIX
                    : color.bottom
                )
              } else if (this.isStacked) {
                return color.bottom
              } else {
                if (!context.chart.chartArea) {
                  return
                }

                const gradient = generateColorGradient({
                  ctx: context.chart.ctx,
                  chartArea: context.chart.chartArea,
                  color
                })

                if (this.isTimeQuery) {
                  const gradientWithTransparency = generateColorGradient({
                    ctx: context.chart.ctx,
                    chartArea: context.chart.chartArea,
                    color,
                    isTransparent: true
                  })
                  return this.chartData.labels.map(label =>
                    label.isInFuture ? gradientWithTransparency : gradient
                  )
                } else {
                  return gradient
                }
              }
            },
            borderRadius:
              this.isStacked && this.isMultiMeasures
                ? { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 }
                : {
                    topLeft:
                      !this.isStacked ||
                      datasetIndex === datasetsNumber - 1 ||
                      (this.isMultiMeasures &&
                        datasetIndex === datasetsNumber % 2)
                        ? BORDER_RADIUS
                        : 0,
                    topRight:
                      !this.isStacked ||
                      datasetIndex === datasetsNumber - 1 ||
                      (this.isMultiMeasures &&
                        datasetIndex === datasetsNumber % 2)
                        ? BORDER_RADIUS
                        : 0,
                    bottomLeft:
                      !this.isStacked ||
                      datasetIndex === 0 ||
                      (this.isMultiMeasures &&
                        datasetIndex === datasetsNumber / 2 + 1)
                        ? BORDER_RADIUS
                        : 0,
                    bottomRight:
                      !this.isStacked ||
                      datasetIndex === 0 ||
                      (this.isMultiMeasures &&
                        datasetIndex === datasetsNumber / 2 + 1)
                        ? BORDER_RADIUS
                        : 0
                  },
            hoverBorderColor: '#141644',
            hoverBorderWidth: 4,
            borderSkipped: false
          } as ChartDataset<'bar'> & DataSet
        })
      }
    }
  }
}
</script>
